import React, { useEffect, useState } from 'react';
import { useAppSelector } from '../../hooks/redux';
import { PoweredByPathfinder } from './PoweredByPathfinder';

/** Status bar shown under the editing canvas. */
const CanvasFooter: React.FC = () => {
  const zoomLevel = useAppSelector((state) => state.ui.zoomLevel);
  const dxfImport = useAppSelector((state) => state.dxfImport);
  const [hideProgressAfterComplete, setHideProgressAfterComplete] = useState(false);

  const isImporting = dxfImport.active && dxfImport.phase !== 'error';
  const isOpenMode = dxfImport.mode === 'open';

  const progressPercent = Math.min(100, Math.max(0, Math.round(dxfImport.progress ?? 0)));

  useEffect(() => {
    if (!isImporting) {
      setHideProgressAfterComplete(false);
      return;
    }
    if (progressPercent >= 100) {
      const timer = window.setTimeout(() => {
        setHideProgressAfterComplete(true);
      }, 1000);
      return () => window.clearTimeout(timer);
    } else {
      setHideProgressAfterComplete(false);
    }
  }, [isImporting, progressPercent, dxfImport.generation]);

  const showProgress = progressPercent > 0 && !hideProgressAfterComplete;
  const zoomLabel = `Zoom ${Math.round(zoomLevel * 100)}%`;

  const statusLabel =
    dxfImport.message
      ? dxfImport.message.replace(/\s*\(\d+%\)|\s*\d+%/g, '').trim()
      : dxfImport.phase === 'uploading'
        ? 'Uploading DXF…'
        : dxfImport.phase === 'fetching'
          ? (isOpenMode ? 'Loading floor plan…' : 'Processing DXF…')
          : dxfImport.phase === 'applying'
            ? 'Drawing floor plan…'
            : isOpenMode
              ? 'Loading floor plan…'
              : 'Importing DXF…';

  return (
    <footer className="shrink-0 h-8 px-4 flex items-center justify-between border-t border-slate-200 bg-white text-[11px] text-slate-500 select-none">
      <div className="flex min-w-0 items-center gap-4">
        <PoweredByPathfinder className="shrink-0 text-slate-600" />

        {isImporting && (
          <div className="flex items-center gap-2.5 border-l border-slate-200 pl-4">
            <span className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse" />
            <span className="font-medium text-slate-700">{statusLabel}</span>
            {showProgress && (
              <>
                <div className="h-1.5 w-28 sm:w-36 overflow-hidden rounded-full bg-slate-100 ring-1 ring-slate-200/80">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-sky-400 via-blue-500 to-indigo-600 transition-all duration-300 ease-out"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                <span className="text-[10px] font-semibold text-slate-500 tabular-nums">
                  {progressPercent}%
                </span>
              </>
            )}
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-3 text-slate-400">
        <span className="text-slate-500">{zoomLabel}</span>
        <span>Del to remove · Esc to deselect</span>
      </div>
    </footer>
  );
};

export default CanvasFooter;
